import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Row, Col, Loader } from '@lucid/lego';
import styles from './UserManagement.module.scss';
import Listing from './Listing/Listing';
import Details from './Details/Details';
import { useStore } from '../../../store/store';
import { useGetPartnerUsers } from '../../../api/services/lupp/onboarding/onboarding';
import { useGetLocations } from '../../../api/services/lupp/locations/locations';
import { toUserLocation, toUserManagementUser } from './utils';
import type { UserLocation, UserManagementUser } from './utils';

/**
 * User Management page component for managing user locations and access.
 * Displays a list of users and their detailed information with editable location.
 *
 * @component
 * @returns {JSX.Element} User Management page
 *
 * @example
 * <UserManagement />
 */
const UserManagement: React.FC = () => {
  const theme = useStore((state) => state.theme);
  const [selectedUser, setSelectedUser] = useState<UserManagementUser | null>(null);
  const [users, setUsers] = useState<UserManagementUser[]>([]);

  const { data: usersData, isLoading, error } = useGetPartnerUsers({ page: 0, perPage: 100 });

  const { data: locationsData } = useGetLocations();

  const transformedUsers = useMemo(
    () => usersData?.items.map(toUserManagementUser) ?? [],
    [usersData?.items]
  );

  const availableLocations = useMemo(
    () => locationsData?.items.map(toUserLocation) ?? [],
    [locationsData?.items]
  );

  // All locations belong to the same Partner Portal organization, needed as the
  // path parameter for PATCH /partners/{organizationIdentifier}/user-locations.
  const organizationIdentifier = locationsData?.items[0]?.organizationIdentifier ?? '';

  useEffect(() => {
    setUsers(transformedUsers);
  }, [transformedUsers]);

  const handleUserSelect = (user: UserManagementUser) => {
    setSelectedUser(user);
  };

  const handleLocationUpdate = (userId: string, newLocations: UserLocation[]) => {
    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === userId ? { ...user, locations: newLocations } : user))
    );

    // Update selected user if it's the one being edited
    if (selectedUser?.id === userId) {
      setSelectedUser((prev) => (prev ? { ...prev, locations: newLocations } : null));
    }
  };

  useEffect(() => {
    if (users.length && !selectedUser) {
      setSelectedUser(users[0]);
    }
  }, [users, selectedUser]);

  if (isLoading) {
    return (
      <div>
        <Box pb={32}>
          <Typography theme={theme} variant="headline" size="small" color="primary-1">
            User Management
          </Typography>
        </Box>
        <Box className={styles.loaderContainer}>
          <Loader size={48} theme={theme} />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Box pb={32}>
          <Typography theme={theme} variant="headline" size="small" color="primary-1">
            User Management
          </Typography>
        </Box>
        <Box className={styles.loaderContainer}>
          <Typography theme={theme} variant="body" size="medium" color="error-1">
            Error loading users. Please try again.
          </Typography>
        </Box>
      </div>
    );
  }

  return (
    <div>
      <Box pb={32}>
        <Typography theme={theme} variant="headline" size="small" color="primary-1">
          User Management
        </Typography>
      </Box>
      <Row gutter={[0, 0]} wrap={false}>
        <Col xs={12} md={3} xxl={4} className={styles.wrapper}>
          <Box pr={16} className={styles.scrollable}>
            <Listing data={users} userDetail={handleUserSelect} selectedUserId={selectedUser?.id} />
          </Box>
        </Col>
        <Col xs={12} md={9} xxl={8} className={styles.wrapper}>
          <Details
            selectedUser={selectedUser}
            availableLocations={availableLocations}
            organizationIdentifier={organizationIdentifier}
            onLocationUpdate={handleLocationUpdate}
          />
        </Col>
      </Row>
    </div>
  );
};

export default UserManagement;
